import React from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Megaphone, MousePointerClick, Target, ShoppingBag, Smartphone,
  Lock, Eye, Activity, Play, FileText, AlertTriangle, ChevronRight,
} from 'lucide-react';
import { TIKTOK_MATRIX, type TiktokObjective } from '@/lib/tiktok-matrix';
import { useTiktokWorkspace } from '@/contexts/TiktokWorkspaceContext';
import { campaignIssues } from './validation';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { formatBudgetHint } from '@/utils/adPlatformMinBudgets';
import { FormField } from '@/components/FormField';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';

type ObjectiveType = TiktokObjective;
type BudgetMode = 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL' | 'BUDGET_MODE_INFINITE';

// Presentation only — the objective list, its order, and which entries are
// usable come from the matrix.
const OBJECTIVE_UI: Record<ObjectiveType, { description: string; icon: React.ReactNode }> = {
  TRAFFIC:         { description: 'Drive clicks to a website or app store',      icon: <MousePointerClick size={18} /> },
  VIDEO_VIEWS:     { description: 'Optimise for 6s / 15s engaged views',         icon: <Play size={18} /> },
  REACH:           { description: 'Maximise unique users seeing the ad',         icon: <Eye size={18} /> },
  ENGAGEMENT:      { description: 'Followers and profile visits',                icon: <Activity size={18} /> },
  CONVERSIONS:     { description: 'Optimise for purchases / sign-ups via Pixel', icon: <Target size={18} /> },
  LEAD_GENERATION: { description: 'In-feed lead form',                           icon: <FileText size={18} /> },
  PRODUCT_SALES:   { description: 'Catalog-driven shopping ads',                 icon: <ShoppingBag size={18} /> },
  APP_PROMOTION:   { description: 'Installs + in-app actions',                   icon: <Smartphone size={18} /> },
};

const OBJECTIVES = TIKTOK_MATRIX.map((o) => ({
  code: o.objective,
  label: o.label,
  description: OBJECTIVE_UI[o.objective].description,
  icon: OBJECTIVE_UI[o.objective].icon,
  // PRODUCT_SALES / APP_PROMOTION need an account asset TikTok can't fake —
  // offering them would only fail later at ad-group time.
  unavailableReason: o.unavailableReason,
  warning: o.warning,
  badge: o.badge,
}));

const BUDGET_MODES: Array<{ value: BudgetMode; label: string; hint: string }> = [
  { value: 'BUDGET_MODE_DAY',      label: 'Daily',     hint: 'Spend up to this amount per day.' },
  { value: 'BUDGET_MODE_TOTAL',    label: 'Lifetime',  hint: 'Total budget over the campaign\'s run.' },
  { value: 'BUDGET_MODE_INFINITE', label: 'Ad group budgets',    hint: 'Set the budget at the ad-group level.' },
];

const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

function Segmented<T extends string>({ options, value, onChange, disabled }: {
  options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; disabled?: boolean;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          disabled={disabled}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'rounded-md px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-50',
            o.value === value
              ? 'bg-surface text-text-primary shadow-soft'
              : 'text-text-muted hover:text-text-primary',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Step 1 of the workspace. Nothing is created here — the review step's build
 * log creates the campaign (ensureCampaignSaved) together with its ad groups
 * and ads. `onNext` moves to the ad groups step after client-side validation.
 */
export const CampaignFormPanel: React.FC<{ onNext: () => void }> = ({ onNext }) => {
  const { campaign, updateCampaignForm } = useTiktokWorkspace();

  const { currency, account } = useTiktokAccountCurrency();
  const budgetHint = formatBudgetHint('tiktok', currency)
    ?? 'Amount is in your advertiser currency.';

  const fd = campaign.formData as Record<string, string>;
  const name           = (fd.campaignName ?? '') as string;
  const objective      = (fd.objectiveType ?? '') as ObjectiveType | '';
  const budgetMode     = ((fd.budgetMode ?? 'BUDGET_MODE_DAY') as BudgetMode);
  const budget         = (fd.budget ?? '') as string;
  const operationStatus = ((fd.operationStatus ?? 'DISABLE') as 'ENABLE' | 'DISABLE');

  const set = (key: string, val: string) => updateCampaignForm({ [key]: val });

  const goNext = () => {
    const issues = campaignIssues(campaign);
    if (issues.length > 0) { toast.error(issues[0]); return; }
    onNext();
  };

  return (
    <div className="tt-campaign-form">
      {/* Name */}
      <Card padded className="tt-campaign-section">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone size={16} className="text-primary" />
          <h3 className="font-semibold text-text-primary">Campaign</h3>
          {campaign.isLocked && (
            <span className="ml-auto flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-2xs font-semibold text-success">
              <Lock size={10} /> Saved on TikTok
            </span>
          )}
        </div>

        <FormField label="Campaign name" required>
          <input
            className={inputCls}
            placeholder="e.g. Eid Sale 2026 — Traffic"
            value={name}
            onChange={e => set('campaignName', e.target.value)}
            disabled={campaign.isLocked}
            aria-label="Campaign name"
            maxLength={512}
          />
        </FormField>
        <div className="mt-5 border-t border-border pt-4">
        <h4 className="text-sm font-semibold text-text-primary mb-1">What is your campaign goal?</h4>
        <p className="mb-3 text-xs text-text-muted">Choose the result you want from your ads.</p>
        <div className="tt-objectives">
          {OBJECTIVES.map(obj => {
            const selected = objective === obj.code;
            const unavailable = !!obj.unavailableReason;
            return (
              <button
                key={obj.code}
                type="button"
                disabled={campaign.isLocked || unavailable}
                title={obj.unavailableReason}
                onClick={() => set('objectiveType', obj.code)}
                className="tt-objective"
                aria-pressed={selected}
              >
                <span className="tt-objective-icon">
                  {obj.icon}
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-1 text-xs font-semibold text-text-primary">
                    {obj.label}
                    {(unavailable || obj.warning) && <AlertTriangle size={11} className="shrink-0 text-warning" />}
                    {obj.badge && (
                      <span className="rounded-full bg-warning-soft px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-warning">
                        {obj.badge}
                      </span>
                    )}
                  </div>
                  <div className={clsx('text-2xs mt-0.5 line-clamp-3', (unavailable || obj.warning) ? 'text-warning' : 'text-text-muted')}>
                    {obj.unavailableReason ? 'Requires catalog access' : obj.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {OBJECTIVES.find((o) => o.code === objective)?.warning && <p className="mt-3 text-xs leading-relaxed text-warning">{OBJECTIVES.find((o) => o.code === objective)?.warning}</p>}
        </div>
      </Card>

      {/* Budget */}
      <Card padded className="tt-campaign-section">
        <h4 className="text-sm font-semibold text-text-primary mb-1">Budget</h4>
        <p className="text-xs text-text-muted mb-3">{BUDGET_MODES.find(b => b.value === budgetMode)?.hint}</p>
        <Segmented<BudgetMode>
          options={BUDGET_MODES.map(b => ({ value: b.value, label: b.label }))}
          value={budgetMode}
          onChange={v => set('budgetMode', v)}
          disabled={campaign.isLocked}
        />

        {budgetMode !== 'BUDGET_MODE_INFINITE' && (
          <div className="mt-4">
            <FormField label="Amount" required hintBelow hint={budgetHint}>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-xs font-medium text-text-muted">
                  {currency ?? '—'}
                </span>
                <input
                  className={`${inputCls} pl-14`}
                  aria-label="Campaign budget amount"
                  type="number"
                  min={0}
                  step="any"
                  placeholder={budgetMode === 'BUDGET_MODE_DAY' ? '50' : '1000'}
                  value={budget}
                  onChange={e => set('budget', e.target.value)}
                  disabled={campaign.isLocked}
                />
              </div>
            </FormField>
            {account && (
              <p className="mt-1 text-2xs text-text-muted">
                Billed by TikTok in {currency} on advertiser {account.advertiserName ?? account.advertiserId}.
              </p>
            )}
          </div>
        )}
        <div className="mt-5 border-t border-border pt-4">
        <h4 className="text-sm font-semibold text-text-primary mb-3">Start status</h4>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={campaign.isLocked}
            aria-pressed={operationStatus === 'DISABLE'}
            onClick={() => set('operationStatus', 'DISABLE')}
            className={clsx(
              'flex-1 rounded-xl border px-3 py-2 text-sm transition-colors disabled:opacity-50',
              operationStatus === 'DISABLE'
                ? 'border-primary bg-primary-soft text-primary'
                : 'border-border bg-surface text-text-primary hover:border-primary/40',
            )}
          >Paused (recommended)</button>
          <button
            type="button"
            disabled={campaign.isLocked}
            aria-pressed={operationStatus === 'ENABLE'}
            onClick={() => set('operationStatus', 'ENABLE')}
            className={clsx(
              'flex-1 rounded-xl border px-3 py-2 text-sm transition-colors disabled:opacity-50',
              operationStatus === 'ENABLE'
                ? 'border-success bg-success-soft text-success'
                : 'border-border bg-surface text-text-primary hover:border-success/40',
            )}
          >Enabled (campaign only)</button>
        </div>
        <p className="mt-2 text-2xs text-text-muted">
          Ad groups and ads have their own status. Use Activate after publishing to enable the whole campaign.
        </p>
        </div>
      </Card>

      {campaign.isLocked && (
        <p className="text-xs text-text-muted">
          This campaign already exists on TikTok — its name, objective and budget are edited from Campaigns.
        </p>
      )}
      <div className="tt-campaign-next flex justify-end">
        <Button variant="primary" onClick={goNext} iconRight={<ChevronRight size={15} />}>
          Next: Ad groups
        </Button>
      </div>
    </div>
  );
};
